import { fetchAll, insertTip } from "./api.js";
import {
  el, clear, icon, calculatePoints, isLocked, formatDate, teamCode, flagFor,
} from "./util.js";

const $ = (id) => document.getElementById(id);

const NAME_KEY = "wc2026_player_name";

const MATCHES_PER_PAGE = 6;

const state = {
  matches: [],
  tips: [],
  results: [],
  loading: false,
  matchPage: 1,
};

// ---- name handling -------------------------------------------------------
function getPlayerName() {
  return localStorage.getItem(NAME_KEY) || "";
}
function setPlayerName(name) {
  localStorage.setItem(NAME_KEY, name.trim());
}

// ---- toast ---------------------------------------------------------------
let toastTimer;
function toast(message, kind = "info") {
  const body = $("toastBody");
  body.textContent = message; // escaped
  body.className =
    "glass-panel-heavy px-5 py-3 rounded-lg font-body-md text-body-md shadow-lg " +
    (kind === "error"
      ? "text-error border-error/40"
      : kind === "success"
      ? "text-tertiary border-tertiary/40"
      : "text-on-surface");
  $("toast").classList.remove("hidden");
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => $("toast").classList.add("hidden"), 3200);
}

// ---- data load -----------------------------------------------------------
async function loadAll() {
  state.loading = true;
  try {
    const data = await fetchAll();
    Object.assign(state, data);
  } catch (e) {
    console.error(e);
    toast("Could not load data. Check connection.", "error");
  } finally {
    state.loading = false;
  }
  renderAll();
}

function renderAll() {
  $("headerName").textContent = getPlayerName() || "Guest";
  $("playerName").value = getPlayerName();
  renderMatches();
  renderLeaderboard();
}

// ---- match card stepper --------------------------------------------------
function stepper(initial, { disabled = false } = {}) {
  let value = Number.isFinite(+initial) && initial !== "" ? Math.max(0, +initial) : 0;
  const display = el("span", {
    class: "font-headline-md text-headline-md w-6 text-center text-on-surface",
    text: String(value),
  });
  const mkBtn = (sym, delta) =>
    el(
      "button",
      {
        type: "button",
        class:
          "w-8 h-8 rounded bg-surface border border-outline-variant flex items-center justify-center text-on-surface hover:border-secondary-container hover:text-secondary-container transition-colors active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed",
        ...(disabled ? { disabled: "disabled" } : {}),
        onClick: () => {
          value = Math.max(0, value + delta);
          display.textContent = String(value);
        },
      },
      [icon(sym, "text-sm")]
    );

  const wrap = el("div", { class: "flex items-center gap-2" }, [
    mkBtn("remove", -1),
    display,
    mkBtn("add", +1),
  ]);
  return { wrap, get: () => value };
}

function teamBadge(team, { dim = false } = {}) {
  return el("div", { class: "flex flex-col items-center gap-sm" }, [
    el(
      "div",
      {
        class:
          "w-16 h-16 rounded-full bg-surface-container border-2 border-outline-variant flex items-center justify-center text-3xl " +
          (dim ? "opacity-90" : ""),
      },
      [flagFor(team)]
    ),
    el("span", { class: "font-headline-md text-headline-md text-on-surface", text: teamCode(team) }),
    el("span", { class: "font-label-md text-[11px] text-on-surface-variant text-center", text: team }),
  ]);
}

function renderMatches() {
  const container = $("matches");
  clear(container);

  const name = getPlayerName();

  if (!state.matches.length) {
    $("matchesCount").textContent = "";
    container.append(
      el("div", { class: "glass-panel rounded-xl p-md text-on-surface-variant" }, [
        "No matches yet.",
      ])
    );
    return;
  }

  // Pagination — clamp the current page, then slice the visible window.
  const totalPages = Math.max(1, Math.ceil(state.matches.length / MATCHES_PER_PAGE));
  if (state.matchPage > totalPages) state.matchPage = totalPages;
  if (state.matchPage < 1) state.matchPage = 1;

  const start = (state.matchPage - 1) * MATCHES_PER_PAGE;
  const pageMatches = state.matches.slice(start, start + MATCHES_PER_PAGE);

  $("matchesCount").textContent = `${state.matches.length} matches · page ${state.matchPage}/${totalPages}`;

  for (const match of pageMatches) {
    const locked = isLocked(match);
    const existingTip = state.tips.find(
      (t) => t.match_id === match.id && t.player_name.toLowerCase() === name.toLowerCase()
    );
    const result = state.results.find((r) => r.match_id === match.id);
    const hasResult = result && result.home_goals != null && result.away_goals != null;

    // Header: status chip + venue/group placeholder (group from match if present)
    const live = locked && !hasResult;
    const statusChip = live
      ? el("div", { class: "flex items-center gap-xs" }, [
          el("span", { class: "w-2 h-2 rounded-full bg-tertiary live-indicator" }),
          el("span", { class: "font-label-md text-label-md text-tertiary", text: "In Progress" }),
        ])
      : hasResult
      ? el("div", { class: "flex items-center gap-xs" }, [
          icon("check_circle", "text-tertiary text-sm"),
          el("span", { class: "font-label-md text-label-md text-tertiary", text: "Final" }),
        ])
      : el("div", { class: "flex items-center gap-xs" }, [
          icon("calendar_today", "text-outline text-sm"),
          el("span", { class: "font-label-md text-label-md text-on-surface-variant", text: formatDate(match.kickoff) }),
        ]);

    const header = el(
      "div",
      { class: "p-sm border-b border-outline-variant/20 flex justify-between items-center bg-surface/30" },
      [statusChip, el("span", { class: "font-label-md text-label-md text-on-surface-variant", text: `Match #${match.id}` })]
    );

    // Teams row
    const teamsRow = el("div", { class: "flex-1 flex justify-between items-center w-full" }, [
      teamBadge(match.home_team),
      hasResult
        ? el("div", { class: "flex flex-col items-center gap-xs px-4" }, [
            el("span", {
              class: "font-stats-xl text-stats-xl text-primary-fixed",
              text: `${result.home_goals} - ${result.away_goals}`,
            }),
            el("span", { class: "font-label-md text-label-md text-tertiary font-bold", text: "FINAL" }),
          ])
        : el("div", { class: "flex flex-col items-center gap-xs px-4" }, [
            el("span", { class: "font-headline-md text-headline-md text-outline-variant", text: "VS" }),
          ]),
      teamBadge(match.away_team, { dim: true }),
    ]);

    // Prediction panel
    let predictionPanel;
    if (existingTip) {
      const pts = hasResult ? calculatePoints(existingTip, result) : null;
      predictionPanel = el(
        "div",
        { class: "w-full sm:w-auto flex flex-col gap-sm border-t sm:border-t-0 sm:border-l border-outline-variant/30 pt-md sm:pt-0 sm:pl-md text-center" },
        [
          el("div", { class: "font-label-md text-label-md text-on-surface-variant", text: "Your Prediction" }),
          el("div", { class: "font-headline-md text-headline-md text-on-surface", text: `${existingTip.home_goals} : ${existingTip.away_goals}` }),
          pts != null
            ? el("div", {
                class:
                  "font-label-md text-label-md font-bold " +
                  (pts === 3 ? "text-tertiary" : pts === 1 ? "text-secondary-container" : "text-outline"),
                text: pts === 3 ? "+3 Exact!" : pts === 1 ? "+1 Tendency" : "0 pts",
              })
            : el("div", { class: "font-label-md text-label-md text-tertiary", text: "Saved ✓" }),
        ]
      );
    } else if (locked) {
      predictionPanel = el(
        "div",
        { class: "w-full sm:w-auto flex items-center gap-1 border-t sm:border-t-0 sm:border-l border-outline-variant/30 pt-md sm:pt-0 sm:pl-md text-on-surface-variant" },
        [icon("lock", "text-[16px]"), el("span", { class: "font-label-md text-label-md", text: "Predictions Locked" })]
      );
    } else {
      const home = stepper(0);
      const away = stepper(0);
      const submit = el(
        "button",
        {
          type: "button",
          class:
            "w-full bg-secondary-container/10 border border-secondary-container/50 text-secondary-container font-label-md text-label-md py-2 rounded-lg hover:bg-secondary-container hover:text-on-secondary-container transition-colors mt-1 active:scale-95 disabled:opacity-40",
          onClick: () => submitTip(match, home.get(), away.get(), submit),
        },
        ["Submit Prediction"]
      );
      predictionPanel = el(
        "div",
        { class: "w-full sm:w-auto flex flex-col gap-sm border-t sm:border-t-0 sm:border-l border-outline-variant/30 pt-md sm:pt-0 sm:pl-md" },
        [
          el("div", { class: "text-center font-label-md text-label-md text-on-surface-variant", text: "Your Prediction" }),
          el("div", { class: "flex items-center justify-center gap-4 bg-surface-container-low rounded-lg p-2" }, [
            home.wrap,
            el("span", { class: "text-outline-variant font-bold", text: ":" }),
            away.wrap,
          ]),
          submit,
        ]
      );
    }

    const accent = live ? el("div", { class: "absolute top-0 left-0 w-1 h-full bg-tertiary" }) : null;

    const card = el(
      "article",
      { class: "glass-panel rounded-xl overflow-hidden relative group" + (existingTip ? "" : locked ? "" : " glow-focus") },
      [
        accent,
        header,
        el("div", { class: "p-md flex flex-col sm:flex-row items-center gap-lg" }, [teamsRow, predictionPanel]),
      ]
    );
    container.append(card);
  }

  if (totalPages > 1) container.append(paginationControls(totalPages));
}

function paginationControls(totalPages) {
  const go = (page) => {
    state.matchPage = page;
    renderMatches();
    $("view-matches")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const arrow = (sym, page, enabled) =>
    el(
      "button",
      {
        type: "button",
        class:
          "w-10 h-10 flex items-center justify-center rounded-lg bg-surface-container text-on-surface-variant hover:text-primary transition-colors disabled:opacity-30 disabled:cursor-not-allowed",
        ...(enabled ? {} : { disabled: "disabled" }),
        onClick: () => enabled && go(page),
      },
      [icon(sym, "text-sm")]
    );

  const numbers = [];
  for (let p = 1; p <= totalPages; p++) {
    const active = p === state.matchPage;
    numbers.push(
      el(
        "button",
        {
          type: "button",
          class:
            "w-10 h-10 flex items-center justify-center rounded-lg font-bold transition-colors " +
            (active
              ? "bg-secondary-container text-on-secondary-container"
              : "bg-surface-container text-on-surface-variant hover:text-primary"),
          onClick: () => go(p),
        },
        [String(p)]
      )
    );
  }

  return el("div", { class: "flex justify-center items-center gap-2 mt-2 flex-wrap" }, [
    arrow("chevron_left", state.matchPage - 1, state.matchPage > 1),
    ...numbers,
    arrow("chevron_right", state.matchPage + 1, state.matchPage < totalPages),
  ]);
}

async function submitTip(match, homeGoals, awayGoals, btn) {
  const name = $("playerName").value.trim();
  if (!name) {
    toast("Enter your name first (top of page).", "error");
    return;
  }
  setPlayerName(name);

  if (isLocked(match)) {
    toast("This match is already locked.", "error");
    return;
  }
  const already = state.tips.find(
    (t) => t.match_id === match.id && t.player_name.toLowerCase() === name.toLowerCase()
  );
  if (already) {
    toast("You already predicted this match.", "error");
    return;
  }

  btn.disabled = true;
  const error = await insertTip({
    playerName: name,
    matchId: match.id,
    homeGoals,
    awayGoals,
  });
  btn.disabled = false;

  if (error) {
    if (error.code === "23505") toast("You already predicted this match.", "error");
    else {
      console.error(error);
      toast("Could not save prediction.", "error");
    }
    return;
  }
  toast("Prediction saved!", "success");
  await loadAll();
}

// ---- leaderboard ---------------------------------------------------------
function renderLeaderboard() {
  const container = $("leaderboard");
  clear(container);

  const resultMap = new Map(state.results.map((r) => [r.match_id, r]));
  const scores = new Map();
  const exact = new Map();

  for (const tip of state.tips) {
    const pts = calculatePoints(tip, resultMap.get(tip.match_id));
    scores.set(tip.player_name, (scores.get(tip.player_name) || 0) + pts);
    if (pts === 3) exact.set(tip.player_name, (exact.get(tip.player_name) || 0) + 1);
  }

  const rows = [...scores.entries()]
    .map(([name, points]) => ({ name, points, exact: exact.get(name) || 0 }))
    .sort((a, b) => b.points - a.points || b.exact - a.exact);

  if (!rows.length) {
    container.append(el("div", { class: "p-md text-on-surface-variant font-body-md text-body-md" }, ["No predictions yet."]));
    return;
  }

  const me = getPlayerName().toLowerCase();
  rows.forEach((row, i) => {
    const isMe = row.name.toLowerCase() === me && me !== "";
    const rankSlot =
      i === 0
        ? el("span", { class: "font-headline-md text-headline-md text-secondary-container w-6 text-center", text: "1" })
        : i === 1
        ? el("span", { class: "font-headline-md text-headline-md text-outline w-6 text-center", text: "2" })
        : i === 2
        ? el("span", { class: "font-headline-md text-headline-md text-outline-variant w-6 text-center", text: "3" })
        : el("span", { class: "font-body-md text-body-md text-on-surface-variant w-6 text-center", text: String(i + 1) });

    const initials = el(
      "div",
      { class: "w-10 h-10 rounded-full bg-surface-variant flex items-center justify-center text-on-surface-variant font-bold border border-outline-variant text-sm" },
      [teamCode(row.name)]
    );

    const rowEl = el(
      "div",
      {
        class:
          "flex items-center justify-between p-sm px-md border-b border-outline-variant/10 transition-colors " +
          (isMe ? "bg-white/5 border-l-2 border-secondary-container" : "hover:bg-white/5"),
      },
      [
        el("div", { class: "flex items-center gap-sm min-w-0" }, [
          rankSlot,
          initials,
          el("div", { class: "flex flex-col min-w-0" }, [
            el("span", {
              class: "font-body-md text-body-md font-semibold truncate " + (isMe ? "text-primary-fixed" : "text-on-surface"),
              text: row.name + (isMe ? " (You)" : ""),
            }),
            el("span", { class: "font-label-md text-[11px] text-tertiary", text: `${row.exact} exact` }),
          ]),
        ]),
        el("span", { class: "font-headline-md text-headline-md " + (isMe ? "text-primary-fixed" : "text-on-surface"), text: String(row.points) }),
      ]
    );
    container.append(rowEl);
  });
}

// ---- navigation (single-page section focus) ------------------------------
function setView(view) {
  // On large screens all three are visible; on small screens we scroll to it.
  const target = $(`view-${view}`);
  if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });

  document.querySelectorAll(".nav-link").forEach((a) => {
    const active = a.dataset.view === view;
    a.classList.toggle("bg-secondary-container", active);
    a.classList.toggle("text-on-secondary-container", active);
    a.classList.toggle("font-bold", active);
    a.classList.toggle("text-on-surface-variant", !active);
  });
  document.querySelectorAll(".mnav").forEach((a) => {
    const active = a.dataset.view === view;
    a.classList.toggle("bg-secondary-container", active);
    a.classList.toggle("text-on-secondary-container", active);
    a.classList.toggle("text-on-surface-variant", !active);
  });
}

// ---- wire up -------------------------------------------------------------
function init() {
  $("saveNameBtn").addEventListener("click", () => {
    const name = $("playerName").value.trim();
    if (!name) {
      toast("Please enter a name.", "error");
      return;
    }
    setPlayerName(name);
    $("headerName").textContent = name;
    renderMatches();
    renderLeaderboard();
    toast(`Hello, ${name}!`, "success");
  });

  $("refreshBtnTop")?.addEventListener("click", loadAll);
  $("refreshBtnMobile")?.addEventListener("click", loadAll);

  document.querySelectorAll("[data-view]").forEach((a) => {
    a.addEventListener("click", (e) => {
      e.preventDefault();
      setView(a.dataset.view);
    });
  });

  loadAll();
}

init();
